import styles from "./Order.module.scss";
import { Order } from "./stripeTransactionHistorySlice";
import { orderStatusColors } from "src/variable/randomColor";
import { formatFullName } from "src/utils";
import { IconButton, Tooltip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ReceiptIcon from "@mui/icons-material/Receipt";
import React from "react";
import { MemberCell } from "src/components/UI/transactionCells";
import { EMPTY_STRING } from "src/components/constant/constant";

const baseColumns = [
  {
    title: "Order ID",
    dataIndex: "order_id",
    key: "order_id"
  },
  {
    title: "Display ID",
    dataIndex: "display_id",
    key: "display_id"
  },
  {
    title: "User",
    dataIndex: "user",
    key: "user",
    render: (_user: Order["user"], data: Order) => (
      <MemberCell
        fullName={formatFullName(data?.user?.attributes?.first_name, data?.user?.attributes?.last_name)}
        email={data?.user?.email}
        userId={data?.user?.user_id}
      />
    )
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    render: (amount: number) => `$${amount?.toFixed(2) || "0.00"}`
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status: string) => (
      <div className={styles.tableStatus}>
        <div
          className={styles.statusBadge}
          style={{
            backgroundColor: `${orderStatusColors[status]}0d`,
            borderColor: `${orderStatusColors[status]}73`,
            color: `${orderStatusColors[status]}FF`
          }}
        >
          {status}
        </div>
      </div>
    )
  }
];

export const createPaymentStatusColumn = (
  onPaymentStatusClick: (_orderId: string, _orderDisplayId: string) => void
) => ({
  title: EMPTY_STRING,
  dataIndex: EMPTY_STRING,
  key: "payment_status",
  render: (_: unknown, record: Order) => (
    <Tooltip title="View Payment Status">
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onPaymentStatusClick(record.order_id, record.display_id);
        }}
        className={styles.paymentStatusIcon}
      >
        <ReceiptIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
});

export const itemsTableColumns = [
  {
    title: "Item ID",
    dataIndex: "order_item_id",
    key: "order_item_id"
  },
  {
    title: "Object ID",
    dataIndex: "object_id",
    key: "object_id"
  },
  {
    title: "Type",
    dataIndex: "order_item_type",
    key: "order_item_type"
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    render: (amount: number) => `$${amount?.toFixed(2) || "0.00"}`
  }
];

type ExpandHandler = (_orderId: string, _isExpanded: boolean) => void;

export const createExpandIcon =
  (expandedRows: Set<string>, onExpand: ExpandHandler) =>
  ({ record }: { record: { order_id: string; __type?: "skeleton" | "empty" } }): React.ReactElement | null => {
    if (record.__type === "skeleton" || record.__type === "empty") {
      return null;
    }
    const orderId = record.order_id;
    const isExpanded = expandedRows.has(orderId);

    return (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onExpand(orderId, isExpanded);
        }}
        className={styles.expandButton}
      >
        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    );
  };

export default baseColumns;
